import "./App.css";
import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppProvider } from "./components/AppContext";
import { Toaster, toast } from "./components/ui/sonner";
import Header from "./components/Header";
import CompareBar from "./components/CompareBar";
import BottomNav from "./components/BottomNav";

// Lazy load pages
const HomePage = lazy(() => import("./components/HomePage"));
const ProductDetail = lazy(() => import("./components/ProductDetail"));
const CategoryPage = lazy(() => import("./components/CategoryPage"));
const CategoriesPage = lazy(() => import("./components/CategoriesPage"));
const AuthPage = lazy(() => import("./components/AuthPage"));
const WishlistPage = lazy(() => import("./components/WishlistPage"));
const CheckoutPage = lazy(() => import("./components/CheckoutPage"));
const OrderConfirmationPage = lazy(() => import("./components/OrderConfirmationPage"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));
const ResetPasswordPage = lazy(() => import("./components/ResetPasswordPage"));
const AllProductsPage = lazy(() => import("./components/AllProductsPage"));
const ComparePage = lazy(() => import("./components/ComparePage"));
const InfoPage = lazy(() => import("./components/InfoPage"));
const BlogListPage = lazy(() => import("./components/BlogPages").then(m => ({ default: m.BlogListPage })));
const BlogPostPage = lazy(() => import("./components/BlogPages").then(m => ({ default: m.BlogPostPage })));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const PayPalCallback = lazy(() => import("./components/PayPalCallback"));
const AdminPanel = lazy(() => import("./components/admin/index"));

const API = process.env.REACT_APP_BACKEND_URL;

const PageLoader = () => (
  <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
    <div className="relative">
      <div className="w-10 h-10 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
      <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-b-[#C9A84C]/20 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}} />
    </div>
  </div>
);

function getSessionId() {
  let sid = sessionStorage.getItem("tt_session");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("tt_session", sid);
  }
  return sid;
}

function ShopLayout() {
  const [cart, setCart] = useState({ items: [] });
  const [cartTrigger, setCartTrigger] = useState(0);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);
  const sessionId = getSessionId();

  // Scroll Reveal Observer
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); obs.unobserve(e.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    const tick = setInterval(() => {
      document.querySelectorAll('.reveal:not(.revealed)').forEach(el => obs.observe(el));
    }, 500);
    return () => { clearInterval(tick); obs.disconnect(); };
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("tt_token");
    const savedUser = localStorage.getItem("tt_user");
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(u => setUser(u))
        .catch(() => { localStorage.removeItem("tt_token"); localStorage.removeItem("tt_user"); setToken(null); setUser(null); });
    }
  }, []);

  useEffect(() => {
    if (!token) { setWishlistIds([]); return; }
    fetch(`${API}/api/wishlist/ids`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setWishlistIds)
      .catch(() => {});
  }, [token]);

  const fetchCart = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/cart/${sessionId}`);
      if (res.ok) setCart(await res.json());
    } catch (e) { console.error("Fetch cart error:", e); }
  }, [sessionId]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = useCallback(async (item) => {
    try {
      const res = await fetch(`${API}/api/cart/${sessionId}/add`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item),
      });
      if (res.ok) {
        setCart(await res.json());
        setCartTrigger(t => t + 1);
        toast.success(item.name ? `${item.name} ajouté au panier` : "Produit ajouté au panier", { duration: 2000 });
      }
    } catch (e) { console.error("Add to cart error:", e); }
  }, [sessionId]);

  const updateQty = useCallback(async (productId, quantity) => {
    try {
      if (quantity <= 0) {
        const res = await fetch(`${API}/api/cart/${sessionId}/remove/${productId}`, { method: "DELETE" });
        if (res.ok) setCart(await res.json());
      } else {
        const res = await fetch(`${API}/api/cart/${sessionId}/update?product_id=${productId}&quantity=${quantity}`, { method: "PUT" });
        if (res.ok) setCart(await res.json());
      }
    } catch (e) { console.error("Update qty error:", e); }
  }, [sessionId]);

  const removeItem = useCallback(async (productId) => {
    try {
      const res = await fetch(`${API}/api/cart/${sessionId}/remove/${productId}`, { method: "DELETE" });
      if (res.ok) setCart(await res.json());
    } catch (e) { console.error("Remove item error:", e); }
  }, [sessionId]);

  const toggleWishlist = useCallback(async (productId) => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/wishlist/toggle/${productId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setWishlistIds(prev => data.action === "added" ? [...prev, productId] : prev.filter(id => id !== productId));
      }
    } catch (e) { console.error("Wishlist toggle error:", e); }
  }, [token]);

  const handleAuth = (userData, authToken) => { setUser(userData); setToken(authToken); };
  const handleLogout = () => {
    localStorage.removeItem("tt_token"); localStorage.removeItem("tt_user");
    setUser(null); setToken(null); setWishlistIds([]);
  };

  const cartData = { ...cart, onUpdateQty: updateQty, onRemove: removeItem };
  const cartCount = (cart?.items || []).reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16 md:pb-0">
      <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: 'inherit' } }} />
      <Header cart={cartData} onOpenCart={cartTrigger} user={user} onLogout={handleLogout} />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage onAddToCart={addToCart} wishlistIds={wishlistIds} onToggleWishlist={toggleWishlist} isLoggedIn={!!token} />} />
          <Route path="/product/:id" element={<ProductDetail onAddToCart={addToCart} wishlistIds={wishlistIds} onToggleWishlist={toggleWishlist} isLoggedIn={!!token} token={token} />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/products" element={<AllProductsPage />} />
          <Route path="/category/:slug" element={<CategoryPage onAddToCart={addToCart} wishlistIds={wishlistIds} onToggleWishlist={toggleWishlist} isLoggedIn={!!token} />} />
          <Route path="/auth" element={<AuthPage onAuth={handleAuth} />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/wishlist" element={<WishlistPage token={token} onAddToCart={addToCart} onToggleWishlist={toggleWishlist} />} />
          <Route path="/checkout" element={<CheckoutPage token={token} />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/profile" element={<ProfilePage token={token} user={user} onAuth={(d) => { setUser(d.user); setToken(d.token); }} />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/info/:slug" element={<InfoPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/auth/callback" element={<AuthCallback onAuth={(u, t) => { setUser(u); setToken(t); }} />} />
          <Route path="/auth/paypal/callback" element={<PayPalCallback onAuth={(u, t) => { setUser(u); setToken(t); }} />} />
          <Route path="*" element={<HomePage onAddToCart={addToCart} wishlistIds={wishlistIds} onToggleWishlist={toggleWishlist} isLoggedIn={!!token} />} />
        </Routes>
      </Suspense>
      <CompareBar />
      <BottomNav cartCount={cartCount} isLoggedIn={!!token} />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return <Suspense fallback={<PageLoader />}><AdminPanel /></Suspense>;
  return <ShopLayout />;
}

function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
